import api from './axios';

// Types
export type GradeLevel = 'K' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8';

export interface RegisteredStudent {
  id: number;
  family_id: number;
  first_name: string;
  last_name: string;
  grade: GradeLevel;
  medical_notes?: string;
  pickup_type: 'car' | 'foot';
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AuthorizedPerson {
  id: number;
  family_id: number;
  user_id?: number; // Link to User account for primary contacts
  first_name: string;
  last_name: string;
  relationship: string;
  phone: string;
  email?: string;
  has_pickup_rights: boolean;
  id_check_required: boolean;
  can_manage_account: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Family {
  id: number;
  primary_contact_id: number;
  status: 'active' | 'inactive' | 'archived';
  created_at: string;
  updated_at: string;
  primaryContact?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    role: string;
  };
  students?: RegisteredStudent[];
  authorizedPersons?: AuthorizedPerson[];
}

export interface CreateFamilyPayload {
  primary_contact_relationship: string; // Required: relationship of primary contact to students
  students: Array<{
    first_name: string;
    last_name: string;
    grade: GradeLevel;
    medical_notes?: string;
    pickup_type?: 'car' | 'foot';
  }>;
  authorized_persons?: Array<{
    first_name: string;
    last_name: string;
    relationship: string;
    phone: string;
    email?: string;
    has_pickup_rights?: boolean;
    id_check_required?: boolean;
  }>;
}

export interface CreateStudentPayload {
  first_name: string;
  last_name: string;
  grade: GradeLevel;
  medical_notes?: string;
  pickup_type?: 'car' | 'foot';
}

export interface UpdateStudentPayload {
  first_name?: string;
  last_name?: string;
  grade?: GradeLevel;
  medical_notes?: string;
  pickup_type?: 'car' | 'foot';
}

export interface CreateAuthorizedPersonPayload {
  first_name: string;
  last_name: string;
  relationship: string;
  phone: string;
  email?: string;
  has_pickup_rights?: boolean;
  id_check_required?: boolean;
}

export interface UpdateAuthorizedPersonPayload {
  first_name?: string;
  last_name?: string;
  relationship?: string;
  phone?: string;
  email?: string;
  has_pickup_rights?: boolean;
  id_check_required?: boolean;
}

// API Functions

// Family endpoints
export const createFamily = async (payload: CreateFamilyPayload): Promise<Family> => {
  const response = await api.post('/api/families', payload);
  return response.data.family;
};

export const getMyFamily = async (): Promise<Family> => {
  const response = await api.get('/api/families/me');
  return response.data.family;
};

export const updateFamilyStatus = async (status: 'active' | 'inactive' | 'archived'): Promise<Family> => {
  const response = await api.put('/api/families/me', { status });
  return response.data.family;
};

// Registered Students endpoints
export const addStudent = async (payload: CreateStudentPayload): Promise<RegisteredStudent> => {
  const response = await api.post('/api/families/me/students', payload);
  return response.data.student;
};

export const getStudents = async (): Promise<RegisteredStudent[]> => {
  const response = await api.get('/api/families/me/students');
  return response.data.students;
};

export const updateStudent = async (id: number, payload: UpdateStudentPayload): Promise<RegisteredStudent> => {
  const response = await api.put(`/api/families/me/students/${id}`, payload);
  return response.data.student;
};

export const deleteStudent = async (id: number): Promise<void> => {
  await api.delete(`/api/families/me/students/${id}`);
};

// Authorized Persons endpoints
export const addAuthorizedPerson = async (payload: CreateAuthorizedPersonPayload): Promise<AuthorizedPerson> => {
  const response = await api.post('/api/families/me/authorized-persons', payload);
  return response.data.authorizedPerson;
};

export const getAuthorizedPersons = async (): Promise<AuthorizedPerson[]> => {
  const response = await api.get('/api/families/me/authorized-persons');
  return response.data.authorizedPersons;
};

export const updateAuthorizedPerson = async (
  id: number,
  payload: UpdateAuthorizedPersonPayload
): Promise<AuthorizedPerson> => {
  const response = await api.put(`/api/families/me/authorized-persons/${id}`, payload);
  return response.data.authorizedPerson;
};

export const deleteAuthorizedPerson = async (id: number): Promise<void> => {
  await api.delete(`/api/families/me/authorized-persons/${id}`);
};

/**
 * Parent Arrival - "I'm Here" feature
 */
export interface ParentArrivalPayload {
  student_ids: number[]; // Array of registered student IDs
  pickup_type: 'car' | 'foot';
}

export interface ParentArrivalResponse {
  message: string;
  students: Array<{
    id: number;
    first_name: string;
    last_name: string;
    grade: string;
    pickup_type: string;
    checked_in_at: string;
    parent_arrived_at: string;
  }>;
  arrival_time: string;
}

export const notifyArrival = async (
  payload: ParentArrivalPayload
): Promise<ParentArrivalResponse> => {
  const { data} = await api.post<ParentArrivalResponse>(
    '/api/families/me/arrive',
    payload
  );
  return data;
};
